<template>
    <ideo-form-group>
        <ideo-form-checkbox v-model="form.settings.nameIsVisible">
            {{ $t('[[[Wyświetlaj nazwę bloczka]]]') }}
        </ideo-form-checkbox>
    </ideo-form-group>
    <ideo-form-group :label="$t('[[[Wybór strony]]]')" :invalid-feedback="form.$errors.first('settings.pageId')" :state="form.$errors.state('settings.pageId')" required>
        <edito-page-selector v-model="form.settings.pageId" :module="module" :preview="false" label="" />
    </ideo-form-group>



    <ideo-form-group :label="$t('[[[Wybierz galerię]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <ideo-autocomplete
                    v-model="galleryId"
                    name="galleryId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać galerię...]]]')"
                    :fetch="(id: number) => fetchGallery(id)"
                    :search="(query: string, limit: number) => searchGalleries(query, limit)"
                >
                    <template #selected="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                </ideo-autocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addRelated()">
                    <i class="fas fa-plus"></i>
                </button>
            </div>
        </div>
    </ideo-form-group>

    <ideo-form-group :label="$t('[[[Powiązane galerie]]]')">
        <div class="list-group">
            <div v-for="item in related" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary"></i>
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązana galeria]]]')">
                        <b>{{ item.title }}</b>
                    </span>
                </span>
                <span class="d-flex align-items-center">
                    <ideo-button variant="danger" size="sm" pill :id="`btn-delete-related-${item.originId}`" :title="$t('[[[Usuń powiązanie]]]')">
                        <i class="fas fa-trash-alt"></i>
                    </ideo-button>
                    <confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.originId}`" :value="item.id" @confirm="removeRelated(item.originId)" />
                </span>
            </div>
            <div class="list-group-item list-group-item-light text-center" v-if="related.length == 0">
                {{ $t('[[[Brak powiązań]]]') }}
            </div>
        </div>
    </ideo-form-group>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Emit, Prop, Watch } from '@/helpers/Decorators';
import { CurrentWebsite } from '@/helpers/Interfaces';
import { FormType } from '@/helpers/Form';
import merge from 'lodash/merge';
import { wait } from '@/helpers/Utils';
import { useWebsitesStore } from '../../../websites/store';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';
import GalleryService, { ListItemModel } from '@/modules/cms/gallery/services/GalleryService';

interface FormSettings
{
    settings: any;
}

@Options({
    name: 'widget-gallery-settings',
    components: {
        'multiselect': Multiselect
    }
})
export default class GallerySettings extends Vue
{
    @Prop() public modelValue!: any;

    public get form(): FormType<FormSettings>
    {
        return this.modelValue;
    }

    public module: string = 'gallery';
    public defaults: any = {
        nameIsVisible: true,
        pageId: null,
        relatedGalleries: []
    };

    public galleryId: number|null = null;
    public related: ListItemModel[] = [];

    public get current(): CurrentWebsite
    {
        return useWebsitesStore().current;
    }

    public async created(): Promise<void>
    {
        await wait(() => this.current.website);
        this.form.settings = merge(this.defaults, this.form.settings);
        this.loadRelated(this.form.id);
    }

    @Watch('form.settings', { deep: true })
    public onSettingsChanged(value: any): void
    {
        this.triggetModel();
    }

    @Emit('update:modelValue')
    public triggetModel(): any
    {
        return this.form;
    }

    public async validate(): Promise<void>
    {
        this.form.validate(schema => ({
            settings: schema.object({
                pageId: schema.string().nullable().required(this.$t('[[[Wybranie strony jest wymagane]]]'))
            })
        }));
    }

    public async fetchGallery(id: number): Promise<ListItemModel>
    {
        const response = await GalleryService.fetchAutocompleteModel(id);

        return response || null;
    }

    public async searchGalleries(query: string, limit: number): Promise<ListItemModel[]>
    {
        return await GalleryService.getAutocompleteList(query, limit);
    }

    public async addRelated():Promise<void>
    {
        if (this.galleryId == null)
            return;

        const response = await GalleryService.fetchAutocompleteModel(this.galleryId);

        if (this.related.find(x => x.originId === response.originId) === undefined &&
            this.form.settings.relatedGalleries.find(x => x === response.originId) === undefined)
        {
            this.related.push(response);
            this.form.settings.relatedGalleries.push(response.originId);
        }

        this.galleryId = null;
    }

    public removeRelated(originId: number): void
    {
        this.related = this.related.filter(x => x.originId != originId);
        this.form.settings.relatedGalleries = this.form.settings.relatedGalleries.filter(x => x != originId);
    }

    public async loadRelated(id: number): Promise<boolean>
    {
        if (id === 0)
            return true;

        try
        {
            this.related = await GalleryService.getRelatedFromWidget(id);
        }
        catch (ex)
        {
            return false;
        }

        return true;
    }
}
</script>
